/**
 * Orders UI Plugin Routes
 *
 * Defines all routes for the orders UI plugin
 */

import { defineRoutes } from '@lognai/plugin-sdk';

/**
 * Plugin identifier
 */
export const pluginId = 'orders';

/**
 * All routes for the orders UI plugin
 */
export const routes = defineRoutes(pluginId, '/orders', [
  // ============================================================
  // MENU ITEM: Sales Orders
  // ============================================================
  {
    path: '/sales',
    component: () =>
      import('./pages/sales-orders/SalesOrderListPage').then((m) => ({
        default: m.SalesOrderListPage,
      })),
    name: 'Sales Orders',
    description: 'Customer sales orders',
    permissions: ['order:sales:read'],
    nav: { icon: { name: 'ShoppingCart', size: 20 }, showInNav: true, order: 1 },
  },
  {
    path: '/sales/create',
    component: () =>
      import('./pages/sales-orders/CreateSalesOrderPage').then((m) => ({
        default: m.CreateSalesOrderPage,
      })),
    name: 'Create Sales Order',
    description: 'Create a new sales order',
    permissions: ['order:sales:create'],
    nav: { showInNav: false },
  },
  {
    path: '/sales/:id/edit',
    component: () =>
      import('./pages/sales-orders/EditSalesOrderPage').then((m) => ({
        default: m.EditSalesOrderPage,
      })),
    name: 'Edit Sales Order',
    description: 'Edit sales order details',
    permissions: ['order:sales:update'],
    nav: { showInNav: false },
  },
  {
    path: '/sales/:id',
    component: () =>
      import('./pages/sales-orders/SalesOrderDetailPage').then((m) => ({
        default: m.SalesOrderDetailPage,
      })),
    name: 'Sales Order Details',
    description: 'Sales order details',
    permissions: ['order:sales:read'],
    nav: { showInNav: false },
  },

  // ============================================================
  // MENU ITEM: Purchase Orders
  // ============================================================
  {
    path: '/purchase',
    component: () =>
      import('./pages/purchase-orders/PurchaseOrderListPage').then((m) => ({
        default: m.PurchaseOrderListPage,
      })),
    name: 'Purchase Orders',
    description: 'Supplier purchase orders',
    permissions: ['order:purchase:read'],
    nav: { icon: { name: 'ShoppingBag', size: 20 }, showInNav: true, order: 2 },
  },
  {
    path: '/purchase/create',
    component: () =>
      import('./pages/purchase-orders/CreatePurchaseOrderPage').then((m) => ({
        default: m.CreatePurchaseOrderPage,
      })),
    name: 'Create Purchase Order',
    description: 'Create a new purchase order',
    permissions: ['order:purchase:create'],
    nav: { showInNav: false },
  },
  {
    path: '/purchase/:id/edit',
    component: () =>
      import('./pages/purchase-orders/EditPurchaseOrderPage').then((m) => ({
        default: m.EditPurchaseOrderPage,
      })),
    name: 'Edit Purchase Order',
    description: 'Edit purchase order details',
    permissions: ['order:purchase:update'],
    nav: { showInNav: false },
  },
  {
    path: '/purchase/:id',
    component: () =>
      import('./pages/purchase-orders/PurchaseOrderDetailPage').then((m) => ({
        default: m.PurchaseOrderDetailPage,
      })),
    name: 'Purchase Order Details',
    description: 'Purchase order details',
    permissions: ['order:purchase:read'],
    nav: { showInNav: false },
  },
  {
    path: '/purchase/:id/receive',
    component: () =>
      import('./pages/purchase-orders/ReceiveGoodsPage').then((m) => ({
        default: m.ReceiveGoodsPage,
      })),
    name: 'Receive Goods',
    description: 'Receive goods for purchase order',
    permissions: ['order:purchase:receive'],
    nav: { showInNav: false },
  },

  // ============================================================
  // MENU ITEM: Backorders
  // ============================================================
  {
    path: '/backorders',
    component: () =>
      import('./pages/backorders/BackorderListPage').then((m) => ({
        default: m.BackorderListPage,
      })),
    name: 'Backorders',
    description: 'Manage backorders',
    permissions: ['order:backorder:read'],
    nav: { icon: { name: 'AlertTriangle', size: 20 }, showInNav: true, order: 3 },
  },
  {
    path: '/backorders/:id',
    component: () =>
      import('./pages/backorders/BackorderDetailPage').then((m) => ({
        default: m.BackorderDetailPage,
      })),
    name: 'Backorder Details',
    description: 'Backorder details',
    permissions: ['order:backorder:read'],
    nav: { showInNav: false },
  },
]);
